import React, { useEffect, useState } from 'react';
import { useUserStore } from 'src/hooks/admin/user';
import { useAdminCommonStore } from 'src/hooks/admin/adminCommon';
import Helper from 'src/helper/Helper';
import useReduxState from 'src/hooks/useReduxState';
import ReactMultiSelectCheckboxes from 'react-multiselect-checkboxes';
import { useObjCommonFilterStore } from '../../../hooks/objCommonFilter';
import { Tooltip } from 'src/component/Global/Tooltip';
import { useDOMAttachToSystemStore } from 'src/hooks/domAttachToSystem';
import { } from 'src/store/slice/system';
import System from 'src/helper/system';

const UserSearchAcct = () => {
    const defaultModel = { accountName: "", accountNumber: "", salesOrg: [] };

    const [{ orgList }, { get_organization_detail }, { }] = useAdminCommonStore();
    const [{ }, { }, { set_search_acct_list, }] = useUserStore();
    const [{ filter_list }, { }, { clear_all_filter, set_filter_list }] = useObjCommonFilterStore();
    const [{ }, { system_alert_indicator, remove_system_alert_indicator }, { }] = useDOMAttachToSystemStore();

    const [disableSearch, setDisableSearch] = useReduxState(true, 'setDisableAccountSearch');
    const { translation:t } = System;
    const [searchData, setSearchData] = useState(defaultModel);
    const [validationError, setValidationError] = useState({ minTwoErr: false, atleastTwoErr: false })

    const keyMap = {
        accountName: "Account Name",
        accountNumber: "Account Number",
        salesOrg: "Sales Org"
    }

    useEffect(() => {
        async function fetchHere() {
            if (orgList?.length <= 0) {
                get_organization_detail();
            }
        }
        fetchHere();
    }, [orgList]);

    useEffect(() => {
        if (!filter_list || Object.entries(filter_list).length === 0) {
            setSearchData(defaultModel)
        } else {
            const searchingData = defaultModel
            searchingData.accountName = filter_list?.accountName?.searchKeyword || ''
            searchingData.accountNumber = filter_list?.accountNumber?.searchKeyword || ''
            searchingData.salesOrg = filter_list?.salesOrg?.searchKeywordArr ? filter_list?.salesOrg?.searchKeywordArr : [];
            setSearchData(searchingData)
        }
    }, [filter_list]);

    const onSearch = (e) => {
        e.preventDefault();
        const isSearchDataEmpty = Object.values(searchData).every(value => !value);

        if (isSearchDataEmpty) {
            system_alert_indicator({
                name: "sponserUserAcctError",
                message: t('asccountNumberOrAccountNameRequired'),
                type: "danger",
                isDisplay: false,
                isDisplayIcon: true
            });
            return;
        }

        const filterList = Object.entries(searchData)
            .filter(([key, value]) => Helper.valueIsNotNull(value)).map((v) => {
                const val = {
                    searchType: keyMap[v[0]],
                    key: v[0],
                    [Array.isArray(v[1]) ? 'searchKeywordArr' : 'searchKeyword']: v[1],
                }
                return val;
            });

        const filterListObject = filterList?.reduce((acc, curr) => {
            acc[curr.key] = curr;
            return acc;
        }, {});

        if (filterList.length === 0) {
            system_alert_indicator({
                name: "sponserUserAcctError",
                message: t('atLeast2CharWithoutZero'),
                type: "danger",
                isDisplay: false,
                isDisplayIcon: true
            });
            return;
        }

        // Clear Alert
        remove_system_alert_indicator("sponserUserAcctError");
        set_filter_list(filterListObject);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSearchData({
            ...searchData,
            [name]: name === "accountNumber" ? value.replace(/[^0-9]/gi, '') : value,
        });
    };

    useEffect(() => {
        if (searchData.accountNumber?.trim()?.length > 0 && searchData.accountNumber?.trim()?.length < 2) {
            setDisableSearch(true);
            return;
        }
        if (searchData.accountName?.length > 0 && searchData.accountName?.length < 2) {
            setDisableSearch(true);
            return;
        }
        if ((searchData.accountNumber?.trim()?.length > 0 || searchData.accountName?.length > 0)) {
            if (searchData.accountNumber?.trim()?.length > 0 && (!/^(?!0+$)[0-9]+$/.test(searchData.accountNumber?.trim()))) {
                setDisableSearch(true);
                return;
            }
            setDisableSearch(false);
            return;
        }
        setDisableSearch(true);
    }, [searchData]);

    const onClear = async () => {
        set_search_acct_list();
        setSearchData(defaultModel)
        setValidationError({ atleastTwoErr: false, minTwoErr: false });
        remove_system_alert_indicator("sponserUserAcctError")
        clear_all_filter();
    };

    const onBlurAccName = (event) => {
        if (event.target.value && event.target.value.length < 2) {
            setValidationError(prevState => ({ ...prevState, minTwoErr: true }));
        } else {
            setValidationError(prevState => ({ ...prevState, minTwoErr: false }));
        }
    }

    const onBlurAccNumber = (event) => {
        if (searchData.accountNumber?.trim()?.length > 0 && (!/^(?!0+$)[0-9]+$/.test(searchData.accountNumber.trim()))) {

            system_alert_indicator({
                name: "sponserUserAcctError",
                message: t('enterKeywordGreaterThanZero'),
                type: "danger",
                isDisplay: false,
                isDisplayIcon: true
            });

        } else if (searchData.accountNumber?.trim()?.length < 2) {
            remove_system_alert_indicator("sponserUserAcctError")
            setValidationError(prevState => ({ ...prevState, atleastTwoErr: true }));
        } else {
            remove_system_alert_indicator("sponserUserAcctError")
            setValidationError(prevState => ({ ...prevState, atleastTwoErr: false }));
        }
    }

    const handleKeyDown = (event) => {
        if (event.key === ' ') {
            event.preventDefault();
        }
    };

    return (
        <>
            <div className='bg-grey2 p-3 darkgray-border border-end-0 border-start-0'>
                <form onSubmit={(e) => { (!(disableSearch)) && onSearch(e) }} >
                    <div className='row'>
                        <div className='col-md-12 d-flex align-items-center'>
                            <h5 className='text-darkgreen mt-0 font-arial me-2'><strong>{t('searchForAccount')}</strong></h5>
                            <Tooltip
                                position={'bottom center'}
                                opener={() => (<i className="fa fa-info-circle me-1 font-13 text-black-stone font-16" />)}
                                content={() => 'Account Name or Account Number is mandatory to perform account search'}
                            />
                        </div>
                        <div className='col-md-4 mb-3'>
                            <label htmlFor="accountNumber">{t('accountNumber')} </label>
                            <input className={"ps-2 form-control form-control-sm mb-0" + (validationError.atleastTwoErr && "border border-danger")}
                                name="accountNumber"
                                placeholder={t('accountNumber')}
                                type="text"
                                value={searchData.accountNumber?.trim()}
                                onChange={handleInputChange}
                                onBlur={onBlurAccNumber}
                                onKeyDown={handleKeyDown}
                            />
                            {validationError.atleastTwoErr && <p className="text-red">
                                {t('atLeast2CharWithoutZero')}
                            </p>}
                        </div>
                        <div className='col-md-4 mb-3'>
                            <label htmlFor="accountName">{t('accountName')} </label>
                            <input className={"ps-2 form-control form-control-sm mb-0 " + (validationError.minTwoErr && "border border-danger")}
                                name="accountName"
                                placeholder={t('accountName')}
                                type="text"
                                value={searchData.accountName}
                                onChange={handleInputChange}
                                onBlur={onBlurAccName}
                            />
                            {validationError.minTwoErr && <p className="text-red">
                                {t('pleaseEnterAtLeast2Char')}
                            </p>}
                        </div>

                        <div className='col-md-4 mb-3 multiselect pe-0'>
                            <label>{t('salesOrg')}</label>

                            <ReactMultiSelectCheckboxes
                                placeholder={t('selectSalesOrg')}
                                value={searchData.salesOrg}
                                options={(orgList && orgList.length > 0) ? orgList.map(v => ({ label: v, value: v })) : []}
                                onChange={(option) => {
                                    handleInputChange({ target: { name: 'salesOrg', value: option } })
                                }} />
                        </div>
                    </div>
                    <div className="row bs5 mt-2">
                        <div className="col-12 text-sm-end">
                            <button type="button" className="bs5 btn btn-secondary me-2 w110" onClick={() => onClear()}>{t('clear')}</button>
                            <button type="submit" className="bs5 btn btn-primary w110" disabled={disableSearch} >{t('search')}</button>
                        </div>
                    </div>
                </form>
            </div>
        </>
    )
}

export default UserSearchAcct;